'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { 
  ShieldCheck, Bot, ScanFace, FileText, 
  CheckCircle2, ArrowRight, Zap, ChevronRight,
  Activity, ShieldAlert, Cpu, HeartPulse,
  Brain, Megaphone, ClipboardCheck, Target,
  Shirt, HelpCircle, Users, Globe, Lock, Headset,
  Check, Clock, User, BarChart2, Mail, Phone,
  Linkedin, Instagram, Youtube, LayoutDashboard,
  Users2, FileSpreadsheet, Shield, LineChart, Settings,
  Menu, X
} from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  price: string | number;
  billing_cycle: string;
  features_json: string[];
}

export default function InstitutionalSite() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

  const wppLink = "https://wa.me/554888796761?text=Quero%20falar%20com%20um%20especialista%20sobre%20o%20sistema%20Nexus%20Safe.";
  const appLink = "https://app.nexussafe.com.br/";

  // Função de mapeamento segura para os ciclos de cobrança
  const getCycleLabel = (cycle: string) => {
    if (!cycle) return 'ciclo';
    
    // Converte para maiúsculo e remove espaços em branco para evitar bugs de formatação
    const safeCycle = cycle.trim().toUpperCase(); 
    
    const map: Record<string, string> = {
      'MONTHLY': 'mês',
      'QUARTERLY': 'trimestre',
      'SEMIANNUALLY': 'semestre',
      'YEARLY': 'ano',
      'SINGLE': 'único'
    };
    
    // Retorna o valor mapeado ou 'ciclo' caso o valor do banco não esteja no dicionário
    return map[safeCycle] || 'ciclo';
  };

  // Busca os planos ativos direto do banco
  useEffect(() => {
    const fetchPublicPlans = async () => {
      try {
        const res = await fetch(`${apiUrl}/plans/public`);
        if (res.ok) {
          const data = await res.json();
          setPlans(data);
        }
      } catch (error) {
        console.error('Erro ao carregar planos', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPublicPlans();
  }, [apiUrl]);

  return (
    <div className="min-h-screen bg-white selection:bg-blue-200 selection:text-blue-900 font-sans text-gray-800 relative flex flex-col overflow-x-hidden">
      
      {/* ================= FIO LATERAL COLORIDO (Do topo ao rodapé, com múltiplas cores e afastado da esquerda) ================= */}
      <div className="hidden sm:block" style={{
        position: 'fixed',
        top: 0,
        left: '24px', 
        width: '6px',
        height: '100vh',
        background: 'linear-gradient(to bottom, #06b6d4, #3b82f6, #8b5cf6, #ec4899)', 
        zIndex: 99999,
        boxShadow: '0 0 15px rgba(59, 130, 246, 0.4)'
      }}></div>

      {/* ================= NAVBAR ================= */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100 shadow-sm top-0">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between relative">
          
          {/* LOGO */}
          <div className="flex items-center shrink-0 h-full">
            <Link href="/" className="flex items-center">
              <img 
                src="/logo-nexus.png" 
                alt="NexusSafe" 
                style={{ height: '40px', width: 'auto' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if(fallback) fallback.style.display = 'flex';
                }}
              />
              <div style={{ display: 'none' }} className="items-center gap-2">
                <ShieldCheck size={28} className="text-gray-900" />
                <span className="text-xl font-black text-gray-900 tracking-tight">
                  NEXUS<span className="font-light">SAFE</span>
                </span>
              </div>
            </Link>
          </div>
          
          {/* LINKS DE NAVEGAÇÃO - Visível a partir do tablet/desktop (md:flex) */}
          <div className="hidden md:flex items-center gap-4 lg:gap-8 font-bold text-sm text-gray-600">
            <Link href="/site/solucoes" className="hover:text-blue-600 transition-colors">Soluções</Link>
            <Link href="/site/para-quem-e" className="hover:text-blue-600 transition-colors">Para quem é</Link>
            <Link href="/site/recursos" className="hover:text-blue-600 transition-colors">Recursos</Link>
            <Link href="/site/conteudos" className="hover:text-blue-600 transition-colors">Conteúdos</Link>
            <Link href="/site/sobre-nos" className="hover:text-blue-600 transition-colors">Sobre nós</Link>
          </div>

          {/* BOTÕES DE AÇÃO E MENU MOBILE */}
          <div className="flex items-center gap-4 text-sm font-bold shrink-0">
            <div className="hidden md:flex items-center gap-4">
              <a href={appLink} className="text-gray-800 hover:text-blue-600 transition-colors border-r border-gray-300 pr-4">
                Entrar
              </a>
              <a href={wppLink} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white px-5 py-2.5 rounded-full hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20 whitespace-nowrap">
                Fale com um especialista <ArrowRight size={16} />
              </a>
            </div>
            {/* Ícone Menu Mobile - Só aparece no celular (md:hidden) */}
            <button 
              className="md:hidden text-gray-800 p-2 transition-colors hover:text-blue-600"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Alternar menu"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* ================= MENU MOBILE (DROPDOWN) ================= */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 w-full bg-white shadow-xl border-t border-gray-100 flex flex-col p-6 gap-4 z-40 max-h-[calc(100vh-80px)] overflow-y-auto">
            <Link href="/site/solucoes" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Soluções <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/para-quem-e" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Para quem é <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/recursos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Recursos <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/conteudos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Conteúdos <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/sobre-nos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Sobre nós <ChevronRight size={16} className="text-gray-400" />
            </Link>
            
            <div className="flex flex-col gap-3 mt-4">
              <a href={appLink} className="w-full text-center text-gray-900 font-bold py-3.5 border-2 border-gray-200 rounded-full hover:bg-gray-50 transition-colors">
                Entrar no Sistema
              </a>
              <a href={wppLink} target="_blank" rel="noopener noreferrer" className="w-full bg-blue-600 text-white py-3.5 rounded-full font-bold hover:bg-blue-700 transition-all flex justify-center items-center gap-2 shadow-lg shadow-blue-600/20">
                Fale com um especialista <ArrowRight size={16} />
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ================= HERO SECTION ================= */}
      <section style={{ paddingTop: '180px', paddingBottom: '80px' }} className="px-6 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* TEXTOS - ESQUERDA */}
        <div className="space-y-6 md:space-y-8">
          <div className="text-blue-600 font-bold text-sm tracking-widest uppercase">
            SST Inteligente
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.1] tracking-tight">
            Segurança que protege pessoas. <br/>
            <span className="text-blue-600">Tecnologia que transforma gestão.</span>
          </h1>
          
          <p className="text-base md:text-lg text-gray-500 font-medium max-w-lg leading-relaxed">
            Somos mais que um software, somos o elo tecnológico que faltava para a sua gestão de SST e RH.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 pt-2 md:pt-4">
            <a href={wppLink} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white px-8 py-4 rounded-full font-bold text-sm hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 text-center">
              Agendar uma demonstração <ArrowRight size={16} />
            </a>
            <Link href="/site/solucoes" className="bg-white text-gray-900 border border-gray-200 px-8 py-4 rounded-full font-bold text-sm hover:border-gray-400 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 text-center">
              Conheça a plataforma <ArrowRight size={16} />
            </Link>
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-6 md:gap-8 pt-8 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0"><Globe size={20} /></div>
              <div>
                <p className="text-sm font-bold text-gray-800">100% online</p>
                <p className="text-xs text-gray-500">Acesso de qualquer lugar</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0"><Lock size={20} /></div>
              <div>
                <p className="text-sm font-bold text-gray-800">Segurança de dados</p>
                <p className="text-xs text-gray-500">LGPD e criptografia</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0"><Headset size={20} /></div>
              <div>
                <p className="text-sm font-bold text-gray-800">Suporte especializado</p>
                <p className="text-xs text-gray-500">Acompanhamento próximo</p>
              </div>
            </div>
          </div>
        </div>

        {/* MOCKUP SISTEMA - DIREITA */}
        <div style={{ borderRadius: '24px' }} className="w-full max-w-[500px] mx-auto lg:ml-auto lg:mr-0 overflow-hidden shadow-2xl border border-gray-200 flex bg-gray-50 h-auto">
          {/* Sidebar Dark */}
          <div style={{ backgroundColor: '#0B132B' }} className="w-16 md:w-20 flex flex-col items-center py-6 gap-6 z-10 text-gray-400 shrink-0">
            <ShieldCheck size={26} className="text-white mb-2" />
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-600/50 cursor-pointer relative">
              <div className="absolute -left-3 md:-left-5 top-1/2 -translate-y-1/2 w-1.5 h-6 bg-white rounded-r-full"></div>
              <LayoutDashboard size={18} />
            </div>
            <Users2 size={18} className="hover:text-white cursor-pointer transition-colors" />
            <FileSpreadsheet size={18} className="hover:text-white cursor-pointer transition-colors" />
            <Shield size={18} className="hover:text-white cursor-pointer transition-colors" />
            <LineChart size={18} className="hover:text-white cursor-pointer transition-colors" />
            <Settings size={18} className="hover:text-white cursor-pointer transition-colors mt-auto" />
          </div>
          {/* Main Dashboard Area */}
          <div className="flex-1 p-5 flex flex-col gap-5 overflow-hidden">
            <div className="flex justify-between items-center gap-2">
              <div>
                <h3 className="text-xl font-black text-gray-900 leading-tight">Bem-vinda, <br/>Fernanda 👋</h3>
              </div>
              <div style={{ borderRadius: '20px' }} className="bg-white border border-gray-200 text-xs font-bold px-3 py-1.5 shadow-sm flex items-center gap-1">
                Visão geral <ChevronRight size={12} className="text-gray-400 rotate-90" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div style={{ borderRadius: '16px' }} className="bg-white p-3 border border-gray-100 shadow-sm">
                <p className="text-[10px] font-bold text-gray-500 mb-1">Colaboradores</p>
                <p className="text-lg font-black text-gray-900 mb-1">1.248</p>
                <div className="text-[9px] text-green-500 font-bold">+12% vs anterior</div>
              </div>
              <div style={{ borderRadius: '16px' }} className="bg-white p-3 border border-gray-100 shadow-sm relative overflow-hidden">
                <p className="text-[10px] font-bold text-gray-500 mb-1">Exames em dia</p>
                <p className="text-lg font-black text-gray-900 mb-1">98%</p>
                <div className="text-[9px] text-green-500 font-bold">+5% vs anterior</div>
                <div style={{ position: 'absolute', right: '-10px', bottom: '-10px', width: '40px', height: '40px', border: '3px solid #3b82f6', borderRadius: '50%', opacity: 0.2 }}></div>
              </div>
            </div>

            <div style={{ borderRadius: '16px', minHeight: '120px' }} className="bg-white p-4 border border-gray-100 shadow-sm flex flex-col">
              <p className="text-xs font-bold text-gray-800 mb-2">Indicadores</p>
              <div className="flex-1 flex items-end gap-2 text-[10px] text-gray-400">
                <div className="w-full h-full relative mt-2">
                  <svg className="w-full h-full absolute inset-0" viewBox="0 0 100 40" preserveAspectRatio="none">
                    <path d="M0,30 Q10,10 20,25 T40,15 T60,20 T80,5 T100,10" fill="none" stroke="#2563eb" strokeWidth="1.5" />
                    <path d="M0,35 Q15,20 30,30 T50,25 T70,35 T90,20 T100,15" fill="none" stroke="#10b981" strokeWidth="1.5" />
                  </svg>
                </div>
              </div>
            </div>

            <div style={{ borderRadius: '16px' }} className="bg-white p-4 border border-gray-100 shadow-sm">
              <p className="text-xs font-bold text-gray-800 mb-3">Atividades Recentes</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div style={{ borderRadius: '8px' }} className="w-6 h-6 bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><FileText size={12}/></div>
                  <div><p className="text-[10px] font-bold text-gray-800">LTCAT atualizado</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <div style={{ borderRadius: '8px' }} className="w-6 h-6 bg-green-50 text-green-600 flex items-center justify-center shrink-0"><CheckCircle2 size={12}/></div>
                  <div><p className="text-[10px] font-bold text-gray-800">ASO realizado</p></div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= SOLUÇÕES COMPLETAS ================= */}
      <section id="solucoes" className="py-24 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16">
            <div className="max-w-2xl">
              <div className="text-blue-600 font-bold text-sm tracking-widest uppercase mb-4">Soluções Completas</div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 leading-tight tracking-tight">
                Funcionalidades que simplificam sua gestão e <span className="text-blue-600">protegem o que importa.</span>
              </h2>
            </div>
            <p className="text-base md:text-lg text-gray-500 font-medium max-w-md leading-relaxed pb-2">
              Tudo o que você precisa para uma gestão de SST eficiente, integrada e centrada nas pessoas.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <div style={{ borderRadius: '24px' }} className="bg-white p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all flex flex-col h-full group">
              <div className="flex flex-row items-center gap-4 mb-5">
                <div style={{ borderRadius: '16px' }} className="w-14 h-14 bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <Brain size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 leading-tight">Psicossocial<br/>+ Plano</h3>
              </div>
              <p className="text-sm text-gray-500 font-medium mb-6">Avalie fatores psicossociais, identifique riscos e crie planos.</p>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Questionários</li>
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Análise de riscos</li>
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Relatórios</li>
              </ul>
            </div>

            <div style={{ borderRadius: '24px' }} className="bg-white p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all flex flex-col h-full group">
              <div className="flex flex-row items-center gap-4 mb-5">
                <div style={{ borderRadius: '16px' }} className="w-14 h-14 bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <Megaphone size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 leading-tight">Canal de<br/>Denúncia</h3>
              </div>
              <p className="text-sm text-gray-500 font-medium mb-6">Receba, acompanhe e trate denúncias com segurança e sigilo.</p>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Anonimato garantido</li>
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Fluxos personalizados</li>
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Em tempo real</li>
              </ul>
            </div>

            <div style={{ borderRadius: '24px' }} className="bg-white p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all flex flex-col h-full group">
              <div className="flex flex-row items-center gap-4 mb-5">
                <div style={{ borderRadius: '16px' }} className="w-14 h-14 bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <ClipboardCheck size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 leading-tight">Checklist de<br/>Processos</h3>
              </div>
              <p className="text-sm text-gray-500 font-medium mb-6">Padronize rotinas, garanta conformidade e reduza falhas.</p>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Editáveis</li>
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Web e mobile</li>
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Evidências e fotos</li>
              </ul>
            </div>

            <div style={{ borderRadius: '24px' }} className="bg-white p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all flex flex-col h-full group">
              <div className="flex flex-row items-center gap-4 mb-5">
                <div style={{ borderRadius: '16px' }} className="w-14 h-14 bg-green-50 text-green-500 flex items-center justify-center shrink-0">
                  <Target size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 leading-tight">Plano de<br/>Ação</h3>
              </div>
              <p className="text-sm text-gray-500 font-medium mb-6">Acompanhe medidas, prazos e responsáveis no sistema.</p>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" /> Atribuições</li>
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" /> Notificações</li>
                <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" /> Progresso geral</li>
              </ul>
            </div>

            {/* Linha Inferior */}
            <div className="sm:col-span-2 lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div style={{ borderRadius: '24px' }} className="bg-white p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all flex flex-col h-full group">
                <div className="flex flex-row items-center gap-4 mb-5">
                  <div style={{ borderRadius: '16px' }} className="w-14 h-14 bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                    <Shirt size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 leading-tight">Entrega EPI<br/>Automatizado</h3>
                </div>
                <p className="text-sm text-gray-500 font-medium mb-6">Controle entregas e validade de EPI automaticamente.</p>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-orange-500 shrink-0 mt-0.5" /> Estoque e CA</li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-orange-500 shrink-0 mt-0.5" /> Assinaturas digitais</li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-orange-500 shrink-0 mt-0.5" /> Treinamentos</li>
                </ul>
              </div>

              <div style={{ borderRadius: '24px' }} className="bg-white p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all flex flex-col h-full group">
                <div className="flex flex-row items-center gap-4 mb-5">
                  <div style={{ borderRadius: '16px' }} className="w-14 h-14 bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <HelpCircle size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 leading-tight">Questionários<br/>Inteligentes</h3>
                </div>
                <p className="text-sm text-gray-500 font-medium mb-6">Aplique e analise questionários com resultados em tempo real.</p>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Modelos editáveis</li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Online/Offline</li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Relatórios gerados</li>
                </ul>
              </div>

              <div style={{ borderRadius: '24px' }} className="bg-white p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all flex flex-col h-full group">
                <div className="flex flex-row items-center gap-4 mb-5">
                  <div style={{ borderRadius: '16px' }} className="w-14 h-14 bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                    <Users size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 leading-tight">Processo de<br/>CIPA Online</h3>
                </div>
                <p className="text-sm text-gray-500 font-medium mb-6">Organize eleições, atas e atividades da CIPA com facilidade.</p>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Eleições completas</li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Calendário legal</li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle2 size={18} className="text-blue-600 shrink-0 mt-0.5" /> Atas e documentos</li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= BENEFÍCIOS (Imagem Hexagonal na Esquerda + Textos na Direita) ================= */}
      <section className="py-20 px-6">
        <div style={{ backgroundColor: '#050B14', borderRadius: '40px' }} className="max-w-7xl mx-auto flex flex-col md:flex-row relative shadow-2xl overflow-hidden p-8 md:p-12 lg:p-16 gap-8 md:gap-12 items-center">
          
          {/* LADO ESQUERDO: Imagem Hexagonal com Borda Colorida */}
          <div className="w-full md:w-[45%] flex justify-center relative z-10">
            {/* Wrapper Externo para a Borda (Gradiente Azul para Verde) */}
            <div style={{
              width: '100%',
              maxWidth: '420px',
              aspectRatio: '1 / 1',
              background: 'linear-gradient(135deg, #0ea5e9, #3b82f6, #10b981)', // Tons de azul e verde
              clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)', // Formato Hexagonal
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto'
            }}>
              {/* Wrapper Interno (A foto em si recortada, levemente menor para formar a borda) */}
              <div style={{
                width: 'calc(100% - 12px)',
                height: 'calc(100% - 12px)',
                clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
                backgroundColor: '#050B14'
              }}>
                <img 
                  src="/beneficios.jpg" 
                  alt="Profissional de SST" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>

          {/* LADO DIREITO: Textos de Benefícios */}
          <div className="w-full md:w-[55%] flex flex-col justify-center relative z-10">
            <div className="text-white font-bold text-sm tracking-widest uppercase mb-4">Benefícios</div>
            <h2 className="text-4xl lg:text-5xl font-black leading-tight tracking-tight mb-12 text-white">
              Mais eficiência, menos riscos, <br/>
              <span className="text-blue-500">mais resultados.</span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10">
              <div>
                <div style={{ backgroundColor: 'rgba(30, 58, 138, 0.2)', borderColor: 'rgba(59, 130, 246, 0.3)', borderWidth: '1px', borderRadius: '16px' }} className="w-12 h-12 flex items-center justify-center text-blue-500 mb-5">
                  <Check size={24} />
                </div>
                <h4 className="text-lg font-bold mb-2 text-white">Reduza riscos e evite multas</h4>
                <p style={{ color: '#9CA3AF' }} className="text-sm font-medium leading-relaxed">Esteja sempre em conformidade com a legislação.</p>
              </div>
              <div>
                <div style={{ backgroundColor: 'rgba(30, 58, 138, 0.2)', borderColor: 'rgba(59, 130, 246, 0.3)', borderWidth: '1px', borderRadius: '16px' }} className="w-12 h-12 flex items-center justify-center text-blue-500 mb-5">
                  <Clock size={24} />
                </div>
                <h4 className="text-lg font-bold mb-2 text-white">Economize tempo e recursos</h4>
                <p style={{ color: '#9CA3AF' }} className="text-sm font-medium leading-relaxed">Automatize processos e elimine tarefas manuais.</p>
              </div>
              <div>
                <div style={{ backgroundColor: 'rgba(30, 58, 138, 0.2)', borderColor: 'rgba(59, 130, 246, 0.3)', borderWidth: '1px', borderRadius: '16px' }} className="w-12 h-12 flex items-center justify-center text-blue-500 mb-5">
                  <User size={24} />
                </div>
                <h4 className="text-lg font-bold mb-2 text-white">Cuide do que importa</h4>
                <p style={{ color: '#9CA3AF' }} className="text-sm font-medium leading-relaxed">Mais segurança e bem-estar para os colaboradores.</p>
              </div>
              <div>
                <div style={{ backgroundColor: 'rgba(30, 58, 138, 0.2)', borderColor: 'rgba(59, 130, 246, 0.3)', borderWidth: '1px', borderRadius: '16px' }} className="w-12 h-12 flex items-center justify-center text-blue-500 mb-5">
                  <BarChart2 size={24} />
                </div>
                <h4 className="text-lg font-bold mb-2 text-white">Tenha dados para decidir</h4>
                <p style={{ color: '#9CA3AF' }} className="text-sm font-medium leading-relaxed">Informações claras para decisões estratégicas.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ================= PLANOS DE ASSINATURA ================= */}
      <section id="planos" className="py-24 bg-gray-50 relative overflow-hidden border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-black uppercase tracking-widest text-xs mb-3 block">Simples e Transparente</span>
            <h2 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Escolha a escala da sua proteção</h2>
            <p className="text-gray-500 font-medium text-lg">Pagamento seguro, liberação imediata e sem taxas escondidas.</p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Cpu size={48} className="animate-spin text-blue-600 mb-4" />
              <p className="font-bold tracking-widest uppercase text-xs animate-pulse">Sincronizando Ofertas...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-200 max-w-2xl mx-auto shadow-sm">
              <ShieldCheck size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-black text-gray-800">Estamos atualizando nossa tabela comercial</h3>
              <p className="text-gray-500 mt-2 font-medium">Nossos pacotes estarão disponíveis em breve. Entre em contato com o suporte.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-center">
              {plans.map((plan, index) => {
                // Estratégia de Destaque: Se for o plano do meio (ou o segundo), ele fica maior
                const isPopular = index === 1 || plans.length === 1; 

                return (
                  <div 
                    key={plan.id} 
                    className={`relative bg-white rounded-[2rem] p-8 md:p-10 transition-all duration-300 ${
                      isPopular 
                        ? 'border-2 border-blue-600 shadow-2xl shadow-blue-900/10 md:-translate-y-4 z-10' 
                        : 'border border-gray-200 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    
                    {isPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-md">
                        Mais Escolhido
                      </div>
                    )}

                    <h3 className="text-2xl font-black text-gray-900 mb-4">{plan.name}</h3>
                    
                    <div className="flex items-start gap-1 mb-8">
                      <span className="text-gray-500 font-bold mt-2">R$</span>
                      <span className="text-6xl font-black text-gray-900 tracking-tighter">
                        {Number(plan.price).toFixed(2).split('.')[0]}
                      </span>
                      <div className="flex flex-col justify-end pb-1">
                        <span className="text-xl font-bold text-gray-500">
                          ,{Number(plan.price).toFixed(2).split('.')[1]}
                        </span>
                        {/* ATUALIZADO AQUI COM A FUNÇÃO DE MAPEAMENTO */}
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          / {getCycleLabel(plan.billing_cycle)}
                        </span>
                      </div>
                    </div>

                    <a 
                      href={`/site/checkout/${plan.id}`}
                      className={`flex items-center justify-center gap-2 w-full py-4 rounded-xl font-black uppercase tracking-widest text-xs transition-all mb-8 ${
                        isPopular 
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300' 
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      Assinar {plan.name} <ChevronRight size={16}/>
                    </a>

                    <div className="space-y-4">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-3">Recursos Incluídos</p>
                      
                      <ul className="space-y-3">
                        {plan.features_json?.map((feat, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" />
                            <span className="text-sm font-semibold text-gray-700 leading-snug">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ================= VAMOS CONVERSAR ================= */}
      <section style={{ paddingTop: '80px', paddingBottom: '160px' }} className="px-6">
        <div 
          style={{ 
            background: 'linear-gradient(135deg, #4338CA 0%, #2563EB 100%)',
            borderRadius: '40px' 
          }} 
          className="max-w-7xl mx-auto p-10 lg:p-16 flex flex-col md:flex-row items-center justify-between relative shadow-2xl shadow-blue-600/40"
        >
          {/* TEXTOS (ESQUERDA) */}
          <div className="w-full md:w-[55%] relative z-10 text-white mb-16 md:mb-0 text-center md:text-left">
            <div className="font-bold text-sm tracking-widest uppercase mb-4 text-blue-200">Vamos conversar?</div>
            <h2 className="text-4xl lg:text-5xl font-black leading-tight tracking-tight mb-6">
              Pronto para transformar a gestão de SST na sua empresa?
            </h2>
            <p className="text-lg text-blue-100 font-medium mb-10 leading-relaxed max-w-xl mx-auto md:mx-0">
              Fale com um especialista e veja como a Nexus Safe pode simplificar sua rotina e gerar resultados.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center md:justify-start">
              <a href={wppLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-white text-blue-700 px-8 py-4 rounded-full font-bold text-sm hover:bg-gray-50 transition-all flex items-center justify-center gap-2 shadow-lg">
                Falar com um especialista <ArrowRight size={16} />
              </a>
              <a href={wppLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto text-white font-bold text-sm hover:text-blue-200 transition-colors flex items-center justify-center gap-2">
                Ou agende uma demonstração <ArrowRight size={16} />
              </a>
            </div>
          </div>

          {/* MOCKUP DO CELULAR (DIREITA) */}
          <div className="w-full md:w-[45%] flex justify-center md:justify-end relative z-20" style={{ minHeight: '400px' }}>
            <div style={{
              position: 'absolute',
              width: '280px',
              height: '560px',
              backgroundColor: '#ffffff',
              border: '12px solid #0f172a',
              borderRadius: '40px',
              bottom: '-100px',
              right: '5%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              overflow: 'hidden',
              transform: 'rotate(8deg)'
            }}>
              
              {/* Notch / Câmera */}
              <div style={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: '45%', height: '24px', backgroundColor: '#0f172a',
                borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px',
                zIndex: 20
              }}></div>
              
              {/* Tela do Celular */}
              <div className="flex-1 bg-gray-50 pt-10 px-4 pb-4 overflow-hidden">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 font-bold">Bem-vinda,</p>
                    <p className="text-lg font-black text-gray-900">Fernanda 👋</p>
                  </div>
                  
                  <div style={{ borderRadius: '16px' }} className="bg-white border border-gray-100 p-3 shadow-sm">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Status Geral</p>
                    <div className="h-3 w-1/2 bg-blue-100 rounded mb-2"></div>
                    <div className="h-2 w-1/3 bg-gray-200 rounded"></div>
                  </div>

                  <div style={{ borderRadius: '16px' }} className="bg-white border border-gray-100 p-4 flex items-center justify-between shadow-sm">
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Exames em dia</p>
                      <p className="text-2xl font-black text-gray-900">98%</p>
                      <p className="text-[10px] text-green-500 font-bold">+5% vs anterior</p>
                    </div>
                    <div style={{ width: '48px', height: '48px', border: '5px solid #3b82f6', borderTopColor: '#dbeafe', borderRadius: '50%' }}></div>
                  </div>

                  <div style={{ borderRadius: '16px' }} className="bg-white border border-gray-100 p-4 shadow-sm">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Pendências</p>
                    <p className="text-xl font-black text-red-500">23</p>
                    <div className="w-full h-12 mt-2 bg-[url('https://www.transparenttextures.com/patterns/graphy.png')] opacity-30"></div>
                  </div>
                  
                  <div style={{ borderRadius: '16px' }} className="bg-white border border-gray-100 p-3 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-blue-100 rounded-full"></div>
                      <div className="h-2 w-20 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-2 w-full bg-gray-100 rounded"></div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Elemento Gráfico de fundo */}
          <div className="absolute right-0 bottom-0 w-64 h-64 border-[40px] border-white opacity-10 rounded-full translate-x-1/3 translate-y-1/3 pointer-events-none"></div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer style={{ backgroundColor: '#020617', paddingTop: '100px', paddingBottom: '40px' }} className="font-medium mt-10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-12 border-b border-gray-800 pb-16">
          
          {/* Logo Branca Fixa */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center mb-6">
              <Link href="/" className="flex items-center">
                <img 
                  src="/logo-nexus-branca.png" 
                  alt="NexusSafe" 
                  style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if(fallback) fallback.style.display = 'flex';
                  }}
                />
                <div style={{ display: 'none' }} className="items-center gap-2">
                  <ShieldCheck size={28} className="text-white" />
                  <span className="text-xl font-black text-white tracking-tight">NEXUSSAFE</span>
                </div>
              </Link>
            </div>
            <p style={{ color: '#9CA3AF' }} className="text-sm leading-relaxed mb-8">
              A plataforma inteligente que simplifica a gestão de Segurança e Saúde do Trabalho e conecta pessoas, dados e resultados.
            </p>
            <div className="flex gap-4">
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Linkedin size={18} /></a>
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Instagram size={18} /></a>
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Youtube size={18} /></a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Soluções</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Psicossocial + Plano</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Canal de Denúncia</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Checklist</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Plano de Ação</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Entrega de EPI</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Questionários</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">CIPA</Link></li>
            </ul>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Para quem é</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Indústrias</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Construtoras</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Comércio</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Serviços</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Saúde</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Educação</Link></li>
            </ul>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Recursos</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm mb-8">
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Materiais</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Webinars</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Cases</Link></li>
            </ul>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Sobre Nós</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Institucional</Link></li>
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Parceiros</Link></li>
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Contato</Link></li>
            </ul>
          </div>

          {/* Contato Direto */}
          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Fale Conosco</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm mb-6">
              <li className="flex items-center gap-3"><Phone size={14} className="shrink-0"/> (48) 9.8879-6761</li>
              <li className="flex items-center gap-3"><Mail size={14} className="shrink-0"/> comercial@nexussafe.com.br</li>
            </ul>
            <a href={wppLink} target="_blank" rel="noopener noreferrer" className="inline-flex bg-blue-600 text-white px-5 py-2.5 rounded-full font-bold text-xs hover:bg-blue-700 transition-all items-center gap-2">
              Fale com um especialista <ArrowRight size={14} />
            </a>
          </div>

        </div>

        <div style={{ color: '#9CA3AF' }} className="max-w-7xl mx-auto px-6 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs">
          <p className="text-center md:text-left">© 2026 NexusSafe - Criado por Leandro Rodrigo de Oliveira - Todos os direitos reservados</p>
          <div className="flex items-center gap-6">
            <a href="https://nexussafe.com.br/site/privacidade" className="text-blue-500 hover:text-white transition-colors whitespace-nowrap">Política de Privacidade</a>
            <a href="https://nexussafe.com.br/site/termos" className="text-blue-500 hover:text-white transition-colors whitespace-nowrap">Termos de Uso</a>
          </div>
        </div>
      </footer>

    </div>
  );
}